import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import { AxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingButton from '../../../components/inputs/LoadingButton';
import Form from '../../../features/admin/approvalSequences/form';
import {
  deleteApprovalSequence,
  getApprovalSequence,
  PostApprovalSequenceArgs,
  putApprovalSequence,
} from '../../../services/admin/ApprovalSequences';
import SuccessFormMessage from '../../../components/messages/SuccessFormMessage';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '../../../components/messages/ErrorMessage';
import RemoveASButton from '../../../features/admin/approvalSequences/RemoveASButton';
import { ApprovalSequenceFormFields } from '../../../Interfaces/admin/ApprovalSequence.interface';
import CopyASButton from '../../../features/admin/approvalSequences/CopyASButton';

const ASUpdate = () => {
  const navigate = useNavigate();
  const { approvalSequenceID } = useParams();
  const toast = useRef<Toast>(null);
  const formMethods = useForm<ApprovalSequenceFormFields>({
    defaultValues: { sequence: [{ step: 1, members: [] }] },
    reValidateMode: 'onSubmit',
    mode: 'onSubmit',
  });
  const { handleSubmit, setValue } = formMethods;

  const approvalSequenceData = useQuery({
    queryKey: ['getApprovalSequence', approvalSequenceID],
    queryFn: ({ signal }) => {
      return getApprovalSequence(approvalSequenceID ?? '', signal);
    },
    onSuccess: (data) => {
      setValue('company', data.company.id);
      setValue('report', data.report);
      const processedData = data.members.reduce(
        (acc, member) => {
          const step = member.step;
          const record = acc.members[step];
          const newMemberData = {
            id: member.assignee
              ? `${member.access.id}--${member.assignee.id}`
              : member.access.id,
            name: member.assignee
              ? member.assignee.name
              : member.access.role ?? member.access.user ?? '',
            assigneeId: member.assignee?.id,
          };

          if (record) {
            record.members.push(newMemberData);
          } else {
            acc.members[step] = {
              step,
              members: [newMemberData],
            };
            acc.latestStep = acc.latestStep < step ? step : acc.latestStep;
          }

          return acc;
        },
        { members: {}, latestStep: 0 } as {
          members: Record<number, ApprovalSequenceFormFields['sequence'][0]>;
          latestStep: number;
        }
      );

      for (let i = 1; i < processedData.latestStep; i++) {
        if (!processedData.members[i]) {
          processedData.members[i] = { step: i, members: [] };
        }
      }

      const sequence = Object.values(processedData.members).sort(
        (a, b) => a.step - b.step
      );

      setValue('sequence', sequence);
    },
    refetchOnWindowFocus: false,
    enabled: !!approvalSequenceID,
  });

  const { mutate, isLoading, isSuccess } = useMutation({
    mutationFn: (data: PostApprovalSequenceArgs) => {
      return putApprovalSequence(approvalSequenceID ?? '', data);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        'Update Approval Sequence Error',
        errorData?.message || 'An error occurred! please try again later',
        3000
      );
    },
  });

  const deleteASResult = useMutation({
    mutationFn: () => {
      return deleteApprovalSequence(approvalSequenceID ?? '');
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };

      showToast(
        'error',
        toast,
        'Remove Approval Sequence Error',
        errorData.message || 'An error occurred! please try again later',
        3000
      );
    },
  });

  const onSubmit = (data: ApprovalSequenceFormFields) => {
    const members = data.sequence.reduce((acc, step) => {
      step.members.forEach((member) => {
        const accessID = member.assigneeId
          ? member.id.replace(`--${member.assigneeId}`, '')
          : member.id;
        acc.push({
          step: step.step,
          access: accessID,
          assigneeId: member.assigneeId,
        });
      });
      return acc;
    }, [] as PostApprovalSequenceArgs['members']);
    const payload: PostApprovalSequenceArgs = {
      companyID: data.company,
      report: data.report,
      members: members,
    };

    mutate(payload);
  };

  if (approvalSequenceData.isFetching) {
    return (
      <div className="flex mx-auto">
        <ProgressSpinner />
      </div>
    );
  }

  if (approvalSequenceData.error) {
    const error = approvalSequenceData.error as AxiosError;
    const errorData = error?.response?.data as { message: string };
    return (
      <div className="pt-8">
        <ErrorMessage
          content={
            errorData.message ||
            'Failed to obtain approval sequence data! Please try again later.'
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-8">
      <FormProvider {...formMethods}>
        <ErrorToast toastRef={toast} />
        <div className="text-35px font-bold text-center mb-5 relative">
          <div>Update Approval Sequence</div>
          {!isSuccess && !deleteASResult.isSuccess && (
            <div className="flex justify-content-center lg:absolute top-0 right-0 mt-4 lg:mt-0 lg:mr-8">
              <RemoveASButton
                onAccept={deleteASResult.mutate}
                isLoading={deleteASResult.isLoading}
              />
            </div>
          )}
          {!isSuccess && (
            <div className="flex justify-content-center lg:absolute top-0 left-0 mt-4 lg:mt-0 lg:ml-8">
              <CopyASButton disableOptionID={approvalSequenceID} />
            </div>
          )}
        </div>
        {!isSuccess && !deleteASResult.isSuccess && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="max-w-600px w-full mx-auto flex flex-column gap-5"
          >
            <Form />
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                onClick={() => {
                  navigate('/admin/approval-sequences');
                }}
                className="w-15rem bluwaiRed border-0 flex m-auto py-3"
              >
                <span className="mx-auto text-22px text-white">Cancel</span>
              </Button>
              <LoadingButton
                isLoading={isLoading}
                label="Update"
                className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                fontSize="text-22px"
              />
            </div>
          </form>
        )}
        {isSuccess && (
          <SuccessFormMessage
            message="The approval sequence has been updated!"
            redirectUrl="/admin/approval-sequences"
            redirectMessage="Click here to return to the approval sequences page"
          />
        )}
        {deleteASResult.isSuccess && (
          <SuccessFormMessage
            message="The approval sequence has been removed!"
            redirectUrl="/admin/approval-sequences"
            redirectMessage="Click here to return to the approval sequence page"
          />
        )}
      </FormProvider>
    </div>
  );
};

export default ASUpdate;
